import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Plus, Copy, FileUp, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, PageHeader, Panel, Empty, Loading, btn, StatusChip } from "../components/kit";
import { ReviewStatus } from "../components/ReviewBits";
import { GoalsPanel, HistoryPanel, ReviewCalendar } from "../components/Growth";

function ReviewList({ scope }) {
  const { data, loading } = useApi(`/reviews?scope=${scope}`);
  if (loading && !data) return <Loading />;
  if (!data.length) return <Empty testid={`reviews-empty-${scope}`}>{scope === "mine" ? "No performance reviews yet." : "No team reviews yet. Start one from a person's page under My Team."}</Empty>;
  return (
    <ul className="space-y-2">{data.map((r) => (
      <li key={r.id}><Link to={`/performance/${r.id}`} className="flex flex-col gap-2 rounded-xl border bg-white p-4 hover:border-[color:var(--ink)] sm:flex-row sm:items-center" data-testid={`review-row-${r.id}`}>
        <span className="flex-1"><span className="font-semibold">{scope === "mine" ? r.template.name : r.employee_name}</span>
          <span className="block text-sm text-muted-foreground">{scope === "mine" ? `Reviewer: ${r.manager_name}` : r.template.name}{r.imported ? ` · reviewed ${fmtDate(r.completed_at)} (uploaded)` : `${r.period ? ` · ${r.period}` : ""} · started ${fmtDate(r.created_at)}`}</span></span>
        <ReviewStatus r={r} />
      </Link></li>))}</ul>
  );
}

function ImportTemplate({ reload }) {
  const ref = useRef(null);
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/review-templates/import", fd);
      toast.message("Reading your matrix. This usually takes under a minute.");
      reload();
      for (let i = 0; i < 60; i++) {
        await new Promise((ok) => setTimeout(ok, 3000));
        const t = (await api.get(`/review-templates/${r.data.id}`)).data;
        if (t.import_status === "ready") { toast.success("Done. Check every competency, then save."); nav(`/performance/templates/${t.id}`); return; }
        if (t.import_status === "failed") { toast.error("We couldn't read that file. Try a clearer PDF or photo, or build the template by hand."); reload(); return; }
      }
      toast.message("Still reading. It'll appear in the list when it's ready.");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); reload(); }
  };
  return <>
    <input ref={ref} type="file" hidden accept=".pdf,.docx,.jpg,.jpeg,.png,.heic,.heif,image/*" onChange={pick} data-testid="template-import-input" />
    <button className={btn.ghost} disabled={busy} onClick={() => ref.current?.click()} data-testid="template-import">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {busy ? "Reading…" : "Import a matrix"}</button>
  </>;
}

function Templates() {
  const { data, loading, reload } = useApi("/review-templates");
  const nav = useNavigate();
  const processing = (data || []).some((t) => t.import_status === "processing");
  useEffect(() => { if (!processing) return undefined; const i = setInterval(reload, 5000); return () => clearInterval(i); }, [processing, reload]);
  const rename = async (t) => { const name = window.prompt("New name for this template", t.name); if (!name || name.trim() === t.name) return; try { await api.patch(`/review-templates/${t.id}`, { name }); toast.success("Renamed."); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (t) => { if (!window.confirm(`Delete the ${t.name} template? Reviews already started or finished aren't affected.`)) return; try { await api.delete(`/review-templates/${t.id}`); toast.success("Template deleted."); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = async (t) => { try { const r = await api.post(`/review-templates/${t.id}/copy`); toast.success("Copied."); nav(`/performance/templates/${r.data.id}`); } catch (e) { toast.error(errMsg(e)); } };
  if (loading && !data) return <Loading />;
  return (
    <Panel title="Review templates" action={<div className="flex flex-wrap gap-2"><ImportTemplate reload={reload} /><Link to="/performance/templates/new" className={btn.dark} data-testid="template-new"><Plus className="h-4 w-4" /> New template</Link></div>} testid="templates-panel">
      <p className="mb-4 text-sm text-muted-foreground">Each template is a role's competencies rated 1 (below), 2 (meets) or 3 (exceeds expectations). Managers only see templates they made themselves. Already have a matrix? Import a PDF, Word file or photo and it's turned into a draft template for you to check.</p>
      {!data.length ? <Empty testid="templates-empty">No templates yet.</Empty> : (
        <ul className="space-y-2">{data.map((t) => (
          <li key={t.id} className="flex flex-col gap-2 rounded-xl border bg-white p-4 sm:flex-row sm:items-center" data-testid={`template-row-${t.id}`}>
            <Link to={`/performance/templates/${t.id}`} className="flex-1 hover:underline"><span className="font-semibold">{t.name}</span>
              <span className="block text-sm text-muted-foreground">{t.competencies.length} competencies{t.competencies.some((c) => c.weight != null) ? " · weighted" : ""} · by {t.owner_name}</span></Link>
            {t.import_status === "processing" && <StatusChip status="in_progress" label="Reading file…" />}
            {t.import_status === "failed" && <StatusChip status="overdue" label="Couldn't read file" />}
            <span className="flex flex-wrap gap-2">
              <button className={btn.ghost} onClick={() => rename(t)} data-testid={`template-rename-${t.id}`}><Pencil className="h-4 w-4" /> Rename</button>
              <button className={btn.ghost} onClick={() => copy(t)} data-testid={`template-copy-${t.id}`}><Copy className="h-4 w-4" /> Copy</button>
              <button className="tap inline-flex items-center gap-2 rounded-lg px-3 text-sm font-semibold text-rose-700 hover:bg-rose-50" onClick={() => remove(t)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /> Delete</button>
            </span>
          </li>))}</ul>)}
    </Panel>
  );
}

export default function Performance() {
  const { user } = useAuth();
  const mgr = user.is_manager || user.is_superadmin;
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || (mgr ? "team" : "mine");
  const tabs = mgr ? [["team", "Team reviews"], ["calendar", "Review calendar"], ["mine", "My reviews"], ["templates", "Templates"]] : [["mine", "My reviews"]];
  return (
    <div>
      <PageHeader eyebrow="Growth" title="Performance reviews" testid="performance-header" />
      {tabs.length > 1 && <nav className="mb-6 flex gap-1 overflow-x-auto border-b" aria-label="Performance">
        {tabs.map(([k, l]) => <button key={k} onClick={() => setSp({ tab: k })} data-testid={`performance-tab-${k}`}
          className={`tap shrink-0 border-b-2 px-4 text-sm font-semibold ${tab === k ? "border-[color:var(--dcbc-red)]" : "border-transparent text-muted-foreground"}`}>{l}</button>)}</nav>}
      {tab === "templates" && mgr ? <Templates /> : tab === "calendar" && mgr ? <ReviewCalendar /> : tab === "team" && mgr ? <ReviewList key="team" scope="team" /> : (
        <div className="space-y-6"><ReviewList key="mine" scope="mine" /><GoalsPanel uid={user.id} /><HistoryPanel uid={user.id} /></div>)}
    </div>
  );
}
